import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MapPin, Clock, MailCheck } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Tag } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "investor", label: "Investor" },
  { id: "business", label: "Business / Partner" },
  { id: "media", label: "Media" },
  { id: "careers", label: "Careers" },
  { id: "general", label: "General" },
];

const inputCls =
  "w-full bg-white border border-forest/15 px-5 py-4 text-sm text-forest placeholder:text-fog/70 focus:outline-none focus:border-emerald transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", category: "general", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry sent", {
        description: "Thank you — we've received your message and sent a confirmation to your inbox.",
      });
      setForm({ name: "", email: "", company: "", category: "general", message: "" });
    } catch (err) {
      toast.error("Something went wrong", {
        description: "Your enquiry could not be sent. Please check the details and try again.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        chapter="08"
        tag="Contact"
        testId="contact-hero"
        lines={[<>Start a</>, <>conversation.</>]}
        lead="Investors, partners, kitchens and curious minds — tell us who you are and what you're building. We reply within two business days."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24 sm:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <Reveal>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog mb-3">I am reaching out as</p>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`contact-category-${c.id}`}
                      onClick={() => setForm((f) => ({ ...f, category: c.id }))}
                      className={`rounded-full border px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
                        form.category === c.id
                          ? "bg-forest text-cream border-forest"
                          : "bg-white border-forest/15 text-moss hover:border-forest/50"
                      }`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <input
                  data-testid="contact-name-input"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Full name"
                  className={inputCls}
                />
                <input
                  data-testid="contact-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="Email address"
                  className={inputCls}
                />
              </div>
              <input
                data-testid="contact-company-input"
                value={form.company}
                onChange={set("company")}
                placeholder="Company / organisation (optional)"
                className={inputCls}
              />
              <textarea
                data-testid="contact-message-input"
                required
                minLength={10}
                rows={6}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us about your enquiry…"
                className={`${inputCls} resize-none`}
              />
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="inline-flex items-center gap-3 rounded-full bg-forest text-cream px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-emerald disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                {sending ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          </Reveal>
        </div>

        <div className="lg:col-span-5 space-y-6">
          <Reveal delay={0.1}>
            <div className="border border-forest/10 bg-white p-8" data-testid="contact-hq-card">
              <MapPin className="h-5 w-5 text-emerald" />
              <h3 className="mt-4 font-display font-bold text-xl text-forest">Headquarters</h3>
              <p className="mt-2 text-sm leading-relaxed text-moss">
                Riyadh, Kingdom of Saudi Arabia
              </p>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-fog">
                Serving the Kingdom & the GCC
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="border border-forest/10 bg-white p-8" data-testid="contact-response-card">
              <Clock className="h-5 w-5 text-emerald" />
              <h3 className="mt-4 font-display font-bold text-xl text-forest">What happens next</h3>
              <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-moss">
                <li className="flex gap-2"><span className="text-amber">1.</span> Your enquiry lands with the right team.</li>
                <li className="flex gap-2"><span className="text-amber">2.</span> You receive an instant confirmation email.</li>
                <li className="flex gap-2"><span className="text-amber">3.</span> We reply in person within two business days.</li>
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="bg-deep text-cream p-8" data-testid="contact-investor-card">
              <MailCheck className="h-5 w-5 text-mint" />
              <h3 className="mt-4 font-display font-bold text-xl">Investors</h3>
              <p className="mt-2 text-sm leading-relaxed text-cream/70">
                Select &ldquo;Investor&rdquo; above and mention your fund or interest area — our
                founding team will share materials and arrange a call.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
